import React, { useState } from 'react';
import GeneralFilter from 'src/containers/UserProfile/FilterView/GeneralFilter';
import COMMON_CONSTANTS from 'common/commonConstants';
import FilterByDate from 'src/containers/UserProfile/FilterView/FilterByDate';
import commonDateUtils from 'common/commonDateUtils';
import { useAccounts } from 'src/queries/account';
import { uniqBy } from 'lodash';
import { appUtils } from 'src/components/index';
import { FEEDBACK_REQUESTED_BY } from 'common/commonFeedbackConstants';

const {
  REVIEW_STATUS, ACCESS, GET_ACCOUNT_SPEC_OPS, DATE_RANGE_FILTERS
} = COMMON_CONSTANTS;

const UpcomingFeedbacksFilters = ({ filters, setFilters }) => {
  const loggedUserId = appUtils.getLoggedUserId();
  const loggedUser = appUtils.getLoggedUser();
  const isAdmin = loggedUser.access === ACCESS.ADMIN;

  const { feedbackStatus, range, requesters } = filters;

  const [requesterSearchText, setRequesterSearchText] = useState('');

  const currentRequesterIds = requesters.map((requester) => requester.id);
  const {
    data: requesterAccounts,
    isFetching: isFetchingRequesterAccounts,
    isRefetching: isRefetchingRequesterAccounts,
    isError: isErrorRequesterAccounts
  } = useAccounts(
    {
      ...(requesters.length ? { notIds: currentRequesterIds } : {}),
      ...(isAdmin
        ? {}
        : {
          ids: [loggedUserId],
          specs: [
            {
              op: GET_ACCOUNT_SPEC_OPS.BELOW_ACCOUNTID,
              accountId: loggedUserId
            }
          ]
        })
    },
    {
      page: {
        size: 30
      },
      search: {
        enabled: true,
        field: 'name',
        value: requesterSearchText
      },
      projection: ['name']
    },
    {
      keepPreviousData: true
    }
  );

  const isInitialFetchingRequesterAccounts = isFetchingRequesterAccounts && !isRefetchingRequesterAccounts;
  const isFetching = isInitialFetchingRequesterAccounts;
  const isError = isErrorRequesterAccounts;
  const isReady = !isFetching && !isError;

  if (!isReady) return null;

  const defaultEnd = commonDateUtils.getUnixDateFromNow(180);

  const validStatus = [REVIEW_STATUS.SENT, REVIEW_STATUS.SCHEDULED];
  const statusOptions = validStatus.map((status) => ({
    id: status,
    label: status,
    checked: feedbackStatus.includes(status)
  }));

  const requesterOptions = uniqBy(
    [
      {
        id: FEEDBACK_REQUESTED_BY.SYSTEM,
        label: 'System',
        checked: requesters.some(
          (requester) => requester.id === FEEDBACK_REQUESTED_BY.SYSTEM
        )
      },
      ...requesters.map((requester) => ({
        id: requester.id,
        label: requester.name,
        checked: true
      })),
      ...requesterAccounts.map((account) => ({
        id: account.id,
        label: account.name,
        checked: requesters.some((requester) => requester.id === account.id)
      }))
    ],
    'id'
  );

  const getTitle = (field) => {
    if (field === 'feedbackStatus') {
      const count = feedbackStatus.length;
      if (count === 0) return 'Filter by Status';
      if (count === 1) return '1 status selected';
      return `${count} statuses selected`;
    }

    if (field === 'requesters') {
      const count = requesters.length;
      if (count === 0) return 'Filter by Requester';
      if (count === 1) return '1 requester selected';
      return `${count} requesters selected`;
    }
  };

  return (
    <div className='flex gap-2 w-full'>
      <GeneralFilter
        multiselect
        drop='up'
        wrapperClasses='w-60'
        title={getTitle('feedbackStatus')}
        options={statusOptions}
        optionsWidth='fit'
        onChange={(option) => {
          if (option.checked) {
            if (feedbackStatus.length === 1) return;
            setFilters({
              ...filters,
              feedbackStatus: feedbackStatus.filter(
                (status) => status !== option.id
              ),
              currentPage: 1
            });
          } else {
            setFilters({
              ...filters,
              feedbackStatus: [...feedbackStatus, option.id],
              currentPage: 1
            });
          }
        }}
      />
      <GeneralFilter
        multiselect
        drop='up'
        wrapperClasses='w-60'
        title={getTitle('requesters')}
        options={requesterOptions}
        optionsWidth='fit'
        onChange={(option) => {
          if (option.checked) {
            setFilters({
              ...filters,
              requesters: requesters.filter(
                (requester) => requester.id !== option.id
              ),
              currentPage: 1
            });
          } else {
            setFilters({
              ...filters,
              requesters: [
                ...requesters,
                { id: option.id, name: option.label }
              ],
              currentPage: 1
            });
          }
        }}
        onSearch={(value) => setRequesterSearchText(value)}
        onDropdownClose={() => setRequesterSearchText('')}
        onClear={() => {
          setFilters({
            ...filters,
            requesters: [],
            currentPage: 1
          });
        }}
      />
      <FilterByDate
        classes='w-60'
        drop='up'
        dateRangeSelected={range}
        onSelectDateRange={(value, start, end) => {
          const startValue = commonDateUtils.getFirstUnixOfDateFromUnix(
            start || commonDateUtils.dateToUnix('2020-01-01')
          );

          let endValue = defaultEnd;

          if (value === DATE_RANGE_FILTERS.CUSTOM.key) {
            endValue = commonDateUtils.getFirstUnixOfDateFromUnix(
              end || defaultEnd
            );
          }

          setFilters({
            ...filters,
            range: {
              value,
              start: startValue,
              end: endValue
            },
            currentPage: 1
          });
        }}
      />
    </div>
  );
};

export default UpcomingFeedbacksFilters;
